<script setup>
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import RoundedTableHeaderRow from "@/components/global-themed/table/RoundedTableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import {computed, ref} from "vue";
import {useStore} from "vuex";
import {useMutation, useQuery} from "@tanstack/vue-query";
import axios from "axios";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import SuggestedKeyword from "@/components/ai-onboarding/partials/SuggestedKeyword.vue";
import SweetToast from "@/SweetToast";
import KeywordsFeedbackModal from "@/components/ai-onboarding/modals/KeywordsFeedbackModal.vue";
import useSitemap from "@/composables/useSitemap";

const emit = defineEmits(['changeStep']);
const store = useStore();

const orderBy = computed(() => store.getters['keywordSuggestions/orderBy']);
const orderDirection = computed(() => store.getters['keywordSuggestions/orderDirection']);
const selectedKeywordIndexes = ref(new Set());
const keywordLimit = ref();
const showFeedbackModal = ref(false);

const unselectedKeywords = computed(() => {
  return suggestedKeywords.value
    .filter(keyword => !selectedKeywordIndexes.value.has(keyword.keyword_index.id))
    .map(keyword => keyword.keyword_index.keyword);
});

const {data: suggestedKeywordData, isLoading: initialLoading} = useQuery({
  queryKey: ['keyword-suggestions'],
  queryFn: async () => {
    const {data} = await axios.get('/ajax/v2/ai-onboarding/keywords');
    const keywords = data.data.keywords;

    // max 50 to select at a time OR if user has less than 50 limits left
    keywordLimit.value = Math.min(data.data.keywordSelectionLimit, keywords.length);

    preselectKeywords(keywords);

    return keywords;
  },
});

const preselectKeywords = (keywords) => {
  const preselectedKeywords = keywords.filter(keyword => keyword.selected);

  // if nothing is selected, preselect the max amount they have credits for
  if (preselectedKeywords.length === 0) {
    selectedKeywordIndexes.value = new Set(
      keywords.slice(0, keywordLimit.value)
        .map(keyword => keyword.keyword_index.id)
    );
    return;
  }

  selectedKeywordIndexes.value = new Set(preselectedKeywords.map(keyword => keyword.keyword_index.id));
}

const suggestedKeywords = computed(() => {
  if (!suggestedKeywordData.value) {
    return [];
  }
  const data = [...suggestedKeywordData.value];

  return data.sort(function (a, b) {
    // used as default positioning on page load
    if (orderBy.value === 'position') {
      if (orderDirection.value === 'asc') {
        return a[orderBy.value] > b[orderBy.value] ? 1 : -1;
      }

      return a[orderBy.value] < b[orderBy.value] ? 1 : -1;
    }

    if (orderDirection.value === 'asc') {
      return a.keyword_index[orderBy.value] > b.keyword_index[orderBy.value] ? 1 : -1;
    }

    return a.keyword_index[orderBy.value] < b.keyword_index[orderBy.value] ? 1 : -1;
  });
});

const {updateAutoAssignStatusMutation} = useSitemap({
  onSuccess: () => {
    changeStep(Steps.value.AI_AUTO_ASSIGN_HOLDING, emit);
  },
});

const {mutate: updateAutoAssignStatus} = updateAutoAssignStatusMutation;

const handleContinue = () => {
  if (unselectedKeywords.value.length >= 5) {
    showFeedbackModal.value = true;
    return;
  }

  storeKeywords();
}

const handleFeedbackSubmitted = () => {
  showFeedbackModal.value = false;
  storeKeywords();
}

const {mutate: storeKeywords, isPending} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/keywords/store', {
    keywordIndexIds: Array.from(selectedKeywordIndexes.value),
  }),
  onSuccess: () => {
    updateAutoAssignStatus();
  },
  onError: ({response}) => {
    if (response.status === 400) {
      SweetToast.fire({
        icon: 'error',
        title: response.data.message,
      });
    }

    throw new Error('Something went wrong');
  },
});

const toggleChecked = (keywordIndexId, checked) => {
  if (checked) {
    selectedKeywordIndexes.value.add(keywordIndexId);
    return;
  }

  selectedKeywordIndexes.value.delete(keywordIndexId);
}

const changeOrderBy = (name) => {
  store.dispatch('keywordSuggestions/setOrderBy', name);
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-text-center tw-py-18 tw-px-14 tw-overflow-y-auto visible-scrollbar">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-7">
        <LoadingBlock v-if="initialLoading" />

        <div
          v-else
          class="tw-flex tw-flex-col tw-gap-4 tw-items-center tw-w-full"
        >
          <div class="tw-text-grey-darkest tw-text-base tw-self-end tw-flex tw-items-center tw-gap-3">
            <p>
              Keywords selected:
            </p>
            <span class="tw-font-bold">
              {{ selectedKeywordIndexes.size }}/{{ keywordLimit }}
            </span>
          </div>

          <DataTable class="tw-w-full !tw-py-0">
            <RoundedTableHeaderRow>
              <TableHeaderCell
                class="tw-py-4 tw-px-8"
                col-span="6"
                name="keyword"
                :order-by="orderBy"
                :order-direction="orderDirection"
                @order-changed="changeOrderBy"
              >
                <span>Keyword</span>
              </TableHeaderCell>
              <TableHeaderCell
                class="tw-py-4 tw-justify-center"
                col-span="2"
                name="difficulty"
                :order-by="orderBy"
                :order-direction="orderDirection"
                @order-changed="changeOrderBy"
              >
                <span>Difficulty</span>
              </TableHeaderCell>
              <TableHeaderCell
                class="tw-py-4 tw-justify-center"
                col-span="2"
                name="search_volume"
                :order-by="orderBy"
                :order-direction="orderDirection"
                @order-changed="changeOrderBy"
              >
                <span>Search Volume</span>
              </TableHeaderCell>
              <TableHeaderCell
                class="tw-py-4 tw-justify-center"
                col-span="2"
              >
                <span>Select</span>
              </TableHeaderCell>
            </RoundedTableHeaderRow>

            <div class="tw-border-2 tw-border-grey-mid tw-border-t-0">
              <SuggestedKeyword
                v-for="aiKeyword in suggestedKeywords"
                :key="aiKeyword.id"
                :ai-keyword="aiKeyword"
                :checked="selectedKeywordIndexes.has(aiKeyword.keyword_index.id)"
                @update:checked="(checked) => toggleChecked(aiKeyword.keyword_index.id, checked)"
              />
            </div>
          </DataTable>
        </div>
      </div>
    </div>

    <NavigationButtons
      :show-back-button="false"
      :disable-continue="selectedKeywordIndexes.size < 1"
      :loading="isPending"
      @next="handleContinue"
    />

    <KeywordsFeedbackModal
      :show-modal="showFeedbackModal"
      :unselected-keywords="unselectedKeywords"
      @submitted="handleFeedbackSubmitted"
      @close="showFeedbackModal = false"
    />
  </div>
</template>
